import { createContext, useContext } from 'react';
import type { WsStatus } from './ws/client';
import type { EffectPayloads } from './effects/types';

export interface CopilotToolCall {
  id: string;
  name: string;
  status: 'running' | 'ok' | 'error';
}

/**
 * An analysis artifact a turn produced (plot / comparison table / quality
 * issues). It rides on the assistant message that produced it so it renders
 * inline under that bubble — not as global "latest" state.
 */
export type CopilotArtifact =
  | { kind: 'plot'; data: EffectPayloads['SHOW_MEASUREMENT_PLOT'] }
  | { kind: 'comparison'; data: EffectPayloads['SHOW_COMPARISON_TABLE'] }
  | { kind: 'quality'; data: EffectPayloads['REPORT_QUALITY_ISSUES'] };

export interface CopilotMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  toolCalls: CopilotToolCall[];
  pending: boolean;
  interrupted?: boolean;
  sourceUserText?: string;
  artifacts?: CopilotArtifact[];
}

export interface CopilotContextValue {
  enabled: boolean;
  conversationUuid: string;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  messages: CopilotMessage[];
  /** True from Send until the first token/tool/done — drives the typing dots
   * during the pre-stream gap (Gemini thinking + tool calls). */
  awaitingReply: boolean;
  sendText: (text: string, voiceMode?: boolean) => void;
  // Voice dictation: setRecording(true) opens the mic and streams audio to the
  // copilot's server-side STT; transcripts arrive as `transcript` frames. The
  // final transcript lands in `pendingTranscript` for the composer to insert
  // (no auto-send).
  sendVoiceChunk: (audioB64: string) => void;
  endVoice: () => void;
  isRecording: boolean;
  setRecording: (recording: boolean) => void;
  /** Live interim transcript shown while dictating. */
  interimTranscript: string;
  /** Final transcript awaiting insertion into the composer. */
  pendingTranscript: string | null;
  clearPendingTranscript: () => void;
  retryLastTurn: () => void;
  status: WsStatus;
  activeStudyUuid: string | null;
  setActiveStudyUuid: (uuid: string | null) => void;
  isLoadingMessages: boolean;
  switchConversation: (uuid: string) => void;
  createNewChat: (studyUuid?: string | null) => void;
}

export const CopilotContext = createContext<CopilotContextValue | null>(null);

export function useCopilot(): CopilotContextValue {
  const ctx = useContext(CopilotContext);
  if (!ctx) {
    throw new Error('useCopilot must be used within a CopilotProvider');
  }
  return ctx;
}
